<template>
    <div class="zygote-multiple-choice" :class="{ correct, incorrect, disabled, active }">
        <div class="question-header flex">
            <div class="question-type">Question {{ configuration.component_type_index }}</div>
        </div>
        <div class="load-simulation flex" @click="go" :class="{ active }">
            <div class="load-simulation-icon">
                {{ active ? "Simulation Loaded" : "Start This Question" }}
            </div>
        </div>
        <slot />
        <zygote-rationale v-if="hasRationale" :configuration="rationale" :key="rationale.component_id"
            :display="complete" :answer="answer" />
        <template v-if="assignment">
            <template v-if="attempts">
                <div class="zygote-selected-answer" v-if="!isMultipleChoice">
                    You have selected:
                    {{ selectedText }}
                </div>
                <div class="flex flex-align multiple-choice-actions">
                    <div @click="submit" class="text-button" :class="{ disabled }">Check Your Answer</div>
                    <div class="ff"></div>
                    <div class="question-points" v-if="complete">
                        {{ points }} / {{ question.component_points }} Point{{ points === 1 ? "" : "s" }}
                    </div>
                    <div class="question-attempts" v-else>
                        {{ question.component_attempts - attempts.length }} Attempt{{
                        question.component_attempts - attempts.length === 1 ? "" : "s"
                        }}
                        Remaining
                    </div>
                </div>
            </template>
            <template v-else>
                <div class="flex flex-align flex-center multiple-choice-disabled center">
                    Only students can submit responses.
                </div>
            </template>
        </template>
        <div class="center" v-else>
            <div class="zygote-selected-answer" v-if="!isMultipleChoice">
                You have selected:
                {{ selectedText }}
            </div>
            <div class="multiple-choice-disabled">
                This question can only be answered by students after it is assigned.
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "ZygoteMultipleChoice",
    methods: {
        submit() {
            this.loading = true;

            this.$http
                .post(
                    `/products/${this.product.product_id}/assignments/${this.assignment.assignment_id}/components/${this.id}`,
                    this.payload
                )
                //TODO Move this logic into the actual assignment store service
                .then((response) => {
                    this.$store.dispatch("assignments/attempt", response.$data)

                    if (this.complete) {
                        this.fetchAnswer()
                    }
                })
                .catch((response) => this.$toasted.error(response.$error))
                .finally(() => (this.loading = false));

        },
        go() {
            const interaction = this.configuration.component_children.find(
                (child) => child.component_type === "zygote-interaction"
            );

            if (interaction) {
                this.$store.dispatch("chapters/simulation", {
                    type: "code",
                    code: interaction.component_data.code,
                    simulation: this.id,
                });
            }
        },
        fetchAnswer() {
            this.loading = true;

            this.$http.get(
                `/products/${this.product.product_id}/components/${this.id}/answer`
            )
                .then((response) => {
                    if (response.$data.length > 0) {
                        this.answer = response.$data[0].value;
                    }
                })
                .catch((response) => this.$toasted.error(response.$error))
                .finally(() => (this.loading = false));
        },
        setRationale() {
            const rationale = this.configuration.component_children.find(
                (child) => child.component_type === "zygote-rationale"
            );

            if (rationale) {
                this.hasRationale = true;
                this.rationale = rationale;
            }
        },
        hasMultipleChoice() {
            const options = this.configuration.component_children
                .filter((child) => child.component_type === "multiple-choice-option")
                .map((component) => component.component_id);

            if (options.length > 0) {
                this.isMultipleChoice = true;
                this.answerFormat = "id";
                this.validSelectedOptions = options;
            }
        },
    },
    props: {
        id: String,
        configuration: Object,
    },
    data() {
        return {
            selected: null,
            loading: false,
            subscription: null,
            hasRationale: false,
            rationale: null,
            answerFormat: "string",
            isMultipleChoice: false,
            validSelectedOptions: [],
            answer: null
        };
    },
    computed: {
        product() {
            return this.$store.getters.product;
        },
        assignment() {
            return this.$store.getters.assignment;
        },
        question() {
            return this.$store.getters.question(this.id);
        },
        active() {
            //Is this question the one whose simulation is currently focused on the right side?
            return this.$store.state.chapters.simulation === this.id;
        },
        disabled() {
            return this.loading || this.complete || !this.active || !this.isValidSelection;
        },
        correct() {
            return this.attempt
                ? this.question.component_points === this.attempt.attempt_raw_points
                : false;
        },
        incorrect() {
            return this.attempt
                ? this.question.component_points !== this.attempt.attempt_raw_points
                : false;
        },
        complete() {
            if (this.assignment) {
                return (
                    this.assignment.submission_state !== "started" ||
                    this.question.attempts.length === this.question.component_attempts ||
                    this.correct
                );
            }

            return true;
        },
        attempts() {
            return this.question ? this.question.attempts : undefined;
        },
        attempt() {
            return this.question ? this.question.attempts.slice().pop() : undefined;
        },
        points() {
            if (this.attempt) {
                return Math.max(0, this.attempt.attempt_points);
            }

            return 0;
        },
        isValidSelection() {
            return this.isMultipleChoice ? this.validSelectedOptions.includes(this.selected) : true;
        },
        payload() {
            return this.isMultipleChoice
                ? { answer_id: this.selected, answer_format: this.answerFormat }
                : { answer_value: this.selected.trim(), answer_format: this.answerFormat };
        },
        hideAnswerChoices() {
            return this.configuration.component_data.hasOwnProperty('hide_answer_choices') &&
                this.configuration.component_data.hide_answer_choices === true;
        },
        selectedText() {
            if (this.hideAnswerChoices) {
                return this.selected ? '1 Item Selected' : 'Nothing Yet';
            } else {
                return this.selected ? this.selected.split("_").join(" ") : "Nothing Yet";
            }
        },
    },
    created() {
        this.setRationale();
        this.hasMultipleChoice();

        if (this.complete) {
            this.fetchAnswer();
        }

        this.$on("select", (option) => (this.selected = option));

        this.subscription = this.$store.subscribeAction((action) => {
            if (action.type === "chapters/interaction") {
                const { simulation, payload } = action.payload;

                if (simulation === this.id) {
                    this.selected = payload.pop();
                }
            }
        });

        if (this.attempt) {
            //If this question has been answered already, set the selected value to the last selected option.
            return (this.selected = this.attempt.attempt_value);
        }
    },
    destroyed() {
        if (this.subscription) {
            this.subscription();
            this.subscription = null;
        }
    },
};
</script>

<style lang="less">
@import "~@/assets/less/variables";

.zygote-multiple-choice {
    counter-increment: question;
    background: var(--card-background-color);
    border-radius: 3px;
    border: 1px solid #444;
    margin: 50px;
    transition: border-color ease 0.2s;

    p,
    ul,
    ol {
        margin: 20px;
    }

    &.active {
        border-color: @base;

        .question-type {
            background: @base;
        }
    }

    &.incorrect {
        border-color: @red;

        .question-type {
            background: @red;
        }

        .load-simulation,
        .load-simulation.active {
            background: fade(@red, 40%);
            color: @f;
            border-bottom-color: @red;
        }
    }

    &.correct {
        border-color: @green;

        .question-type {
            background: @green;
        }

        .load-simulation,
        .load-simulation.active {
            background: fade(@green, 40%);
            color: @f;
            border-bottom-color: @green;
        }
    }

    .question-header {
        height: 0;
    }

    .question-type {
        font-size: 11px;
        font-weight: 600;
        color: @f;
        height: 24px;
        line-height: 24px;
        padding: 0 10px;
        background: @grey;
        border-radius: 12px;
        transition: background-color ease 0.2s;
        top: -12px;
        z-index: 3;
    }

    .simulation-button {
        margin: 30px 20px 20px;
        padding: 15px 20px;
        background: var(--modal-background-color);
        border: 1px solid var(--input-border-color);
        border-radius: 3px;
        color: @base;
        font-size: 16px;
        font-weight: 500;
        cursor: pointer;
        transition: all ease 0.2s;

        &:hover,
        &:active {
            background: var(--modal-background-color);

            .simulation-button-arrow {
                transform: translate(10px, 0);
            }
        }
    }

    .zygote-selected-answer {
        font-weight: 500;
        color: var(--primary-text-color);
        padding: 20px;
    }
}

.answer-choices {
    padding: 20px;
}

.multiple-choice-actions {
    padding: 15px 20px;
}

.multiple-choice-disabled {
    padding: 30px;
    font-size: 12px;
}

.question-attempts,
.question-points {
    color: var(--secondary-text-color);
    font-size: 14px;
}

.load-simulation {
    padding: 25px 20px 10px;
    border-bottom: 1px solid #444;
    color: @base;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all ease 0.2s;

    .load-simulation-icon {
        padding-right: 30px;
        background: url("~@/assets/icons/base/arrow-right.svg") no-repeat;
        background-position: right 5px top -1px;
        background-size: 17px auto;
        transition: all ease 0.2s;
    }

    &:hover {
        background: #222;

        .load-simulation-icon {
            background-position: right 0 top -1px;
        }
    }

    &.active {
        background: fade(@base, 40%);
        color: @f;
        border-bottom-color: @base;

        .load-simulation-icon {
            background: transparent;
        }
    }
}

@media only screen and (max-width: 1100px) {
    .multiple-choice {
        margin: 30px;
    }
}
</style>
